import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import WakingScreen from '../components/staging/WakingScreen';

// Staging alone gets the waking experience. Same gate as StagingTape — the value is
// substituted at build time, so a production bundle carries `undefined === 'true'`
// and can never take this branch.
const IS_STAGING = import.meta.env.VITE_STAGING === 'true';

// Error messages for different authentication states
const ERROR_MESSAGES: Record<string, { title: string; message: string; type: 'error' | 'warning' | 'info' }> = {
  pending_activation: {
    title: 'Account Pending Activation',
    message: 'Your account is pending activation by an administrator. You will receive a welcome email when your access is approved.',
    type: 'warning'
  },
  not_invited: {
    title: 'Account Not Set Up',
    message: 'Your account has not been set up in this system. Please contact an administrator to request access.',
    type: 'error'
  },
  not_authorized: {
    title: 'Account Not Found',
    message: 'Your account has not been set up yet. Please contact your administrator to be added to the system.',
    type: 'error'
  },
  domain_not_allowed: {
    title: 'Access Denied',
    message: 'Your email domain is not authorized to access this system. Please contact your administrator if you require access.',
    type: 'error'
  },
  authentication_failed: {
    title: 'Authentication Failed',
    message: 'There was a problem signing you in. Please try again.',
    type: 'error'
  },
  // Sent by the OAuth callback when the backend could not reach its database, so
  // sign-in was never attempted. Nothing about the user's account is wrong, and
  // "Authentication Failed" would send them looking for a problem that isn't there.
  // On staging the database rests STOPPED by design and must be started first
  // (docs/ENVIRONMENTS.md); in production the same wording is still true and still
  // actionable, so the copy names staging without assuming it.
  service_unavailable: {
    title: 'System Temporarily Unavailable',
    message: 'The system could not reach its database, so you could not be signed in. If this is the staging environment, its database is likely asleep and must be started before sign-in works. Please try again in a few minutes, or contact your administrator.',
    type: 'warning'
  },
  session_error: {
    title: 'Session Error',
    message: 'There was a problem with your session. Please try signing in again.',
    type: 'error'
  },
  session_expired: {
    title: 'Session Expired',
    message: 'Your session has expired due to inactivity. Please sign in again.',
    type: 'info'
  }
};

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<{ title: string; message: string; type: 'error' | 'warning' | 'info' } | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Check for error in URL params
  useEffect(() => {
    const errorCode = searchParams.get('error');
    if (errorCode && ERROR_MESSAGES[errorCode]) {
      setError(ERROR_MESSAGES[errorCode]);
      setErrorCode(errorCode);
    }
  }, [searchParams]);

  // Staging's database rests STOPPED by design, so `service_unavailable` there is
  // almost always a sleeping database rather than an outage — and it is something the
  // page itself can fix. Show the waking experience instead of the plain warning, and
  // actually wake the database while it is on screen (WakingScreen does both).
  // Production keeps the sober copy verbatim.
  const showWakingScreen = IS_STAGING && errorCode === 'service_unavailable' && error !== null;

  // Silent backend warmup - wake up Cloud Run while user views login page
  useEffect(() => {
    const warmupBackend = async () => {
      try {
        // Use HEAD request for minimal data transfer
        await fetch(getApiUrl('/api/health'), {
          method: 'HEAD',
          mode: 'cors',
          credentials: 'omit'
        });
      } catch {
        // Silently ignore - warmup is best-effort
      }
    };
    warmupBackend();
  }, []);

  const handleGoogleLogin = () => {
    window.location.href = getApiUrl('/api/auth/google');
  };

  const dismissError = () => {
    setError(null);
    setErrorCode(null);
    // Also clear the URL params
    window.history.replaceState({}, '', '/login');
  };

  const getErrorStyles = (type: 'error' | 'warning' | 'info') => {
    switch (type) {
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: 'fa-circle-exclamation text-red-500',
          title: 'text-red-800',
          text: 'text-red-700'
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          icon: 'fa-clock text-amber-500',
          title: 'text-amber-800',
          text: 'text-amber-700'
        };
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: 'fa-info-circle text-blue-500',
          title: 'text-blue-800',
          text: 'text-blue-700'
        };
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="max-w-md w-full px-4">
        <div className="bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img
              src="/provia-logo.png"
              alt="Provia RCM Logo"
              className="h-16 w-auto"
            />
          </div>

          {/* App Name - matches OAuth consent screen */}
          <div className="mb-4">
            <h1 className="text-xl font-semibold text-gray-800 text-center">
              Deposit Reconciliation Management System
            </h1>
          </div>

          {/* App Description - required for Google OAuth verification */}
          <p className="text-sm text-gray-600 text-center mb-6">
            Healthcare revenue cycle management platform for tracking, reconciling,
            and managing deposit transactions across multiple payer sources.
          </p>

          {/* Staging only: the sleeping database announces itself, and wakes. */}
          {showWakingScreen && <WakingScreen onDismiss={dismissError} />}

          {/* Error Message */}
          {!showWakingScreen && error && (
            <div className={`mb-6 p-4 rounded-lg border ${getErrorStyles(error.type).bg} ${getErrorStyles(error.type).border}`}>
              <div className="flex items-start gap-3">
                <i className={`fas ${getErrorStyles(error.type).icon} mt-0.5`}></i>
                <div className="flex-1">
                  <h3 className={`font-medium ${getErrorStyles(error.type).title}`}>
                    {error.title}
                  </h3>
                  <p className={`text-sm mt-1 ${getErrorStyles(error.type).text}`}>
                    {error.message}
                  </p>
                </div>
                <button
                  onClick={dismissError}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleLogin}
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-gray-700 font-medium">Sign in with Google</span>
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Authorized users only. All activity is monitored and logged.
            </p>
          </div>
        </div>

        {/* Footer with Privacy Policy Link - using <a> tag for Google crawler */}
        <div className="text-center space-y-2">
          <p className="text-xs text-gray-500">
            For technical support, contact your system administrator
          </p>
          <div className="flex justify-center items-center gap-3 text-xs">
            <a
              href="/privacy"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              Privacy Policy
            </a>
            <span className="text-gray-300">|</span>
            <span className="text-gray-500">&copy; {new Date().getFullYear()} Provia RCM Partners</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
